/**
 * Shared "computed" dashboard columns: Service Failure (SF) and Trouble Ticket (TT).
 *
 * These mirror the special columns from the At Risk Monitor and are offered as
 * optional, user-addable columns on the Dispatch and LC dashboards. Like the
 * synthetic "Req" column (see reqColumn.ts), `sf`/`tt` are not real DB fields:
 *
 *  - They are added to a host's `availableFields` so the column picker offers them
 *    (spread {@link COMPUTED_FIELD_OPTIONS}).
 *  - They are rendered by the custom cells from {@link buildComputedColumnDefs},
 *    which a host spreads into its `customColumnDefs` map.
 *  - They pass straight through to the backend in `additional_fields`; the backend
 *    strips the sentinels and attaches the detail the badges read
 *    (`reschedule_count`/`reschedule_history`, `trouble_ticket_count`/`trouble_tickets`).
 *    See backend `app/utils/service_failure_tt.py`.
 *
 * Because they are ordinary `additionalFields` entries, each host's existing
 * additionalFields persistence carries them with no extra work.
 */
import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { ServiceFailureBadge } from '../components/ServiceFailureBadge';
import { TroubleTicketBadge } from '../components/TroubleTicketBadge';

/** Synthetic field ids sent to the backend in `additional_fields`. */
export const SF_FIELD = 'sf';
export const TT_FIELD = 'tt';

/**
 * Column-picker options for the computed columns. Spread these into a host's
 * `availableFields` (they use the same `{fieldname, displayname, abbreviation, type}`
 * shape the field-definition-derived options use).
 */
export const COMPUTED_FIELD_OPTIONS = [
  {
    fieldname: SF_FIELD,
    displayname: 'Service Failures (SF)',
    abbreviation: 'SF' as string | null,
    type: 'number',
  },
  {
    fieldname: TT_FIELD,
    displayname: 'Trouble Tickets (TT)',
    abbreviation: 'TT' as string | null,
    type: 'number',
  },
];

interface ComputedColumnOptions {
  /** Dark-mode flag — the badge tooltips are portaled, so colors are inline. */
  /** Opens the order drawer for a pronumber (wired to the TT badge click). */
  onProClick?: (pronumber: string) => void;
}

/**
 * Build the `customColumnDefs` entries for the SF and TT columns, keyed by their
 * field ids so a host can spread them into its `customColumnDefs` record.
 */
export function buildComputedColumnDefs({
  onProClick,
}: ComputedColumnOptions): Record<string, ColumnDef<any>> {
  return {
    [SF_FIELD]: {
      id: SF_FIELD,
      header: 'SF',
      size: 60,
      enableSorting: true,
      enableColumnFilter: false,
      // Sort by the reschedule count; the cell renders the full badge + tooltip.
      accessorFn: (row: any) => row.reschedule_count ?? 0,
      cell: ({ row }) => (
        <ServiceFailureBadge
          history={row.original.reschedule_history || []}
          count={row.original.reschedule_count || 0}
          placement="right"
        />
      ),
      aggregatedCell: () => null,
    },
    [TT_FIELD]: {
      id: TT_FIELD,
      header: 'TT',
      size: 60,
      enableSorting: true,
      enableColumnFilter: false,
      accessorFn: (row: any) => row.trouble_ticket_count ?? 0,
      cell: ({ row }) => (
        <TroubleTicketBadge
          tickets={row.original.trouble_tickets || []}
          count={row.original.trouble_ticket_count || 0}
          onClick={onProClick ? () => onProClick(String(row.original.pronumber)) : undefined}
          placement="right"
        />
      ),
      aggregatedCell: () => null,
    },
  };
}
